import React, { useState, useEffect } from "react";
import ApiClient from "../api/ApiClient";

const pageSizeOptions = [10, 25, 100, 500];

export const ModelExecution = () => {
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState("");
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [executionError, setExecutionError] = useState(null);
  const [useOriginalCtracPackage, setUseOriginalCtracPackage] = useState(false);
  const [useRealTimeCandles, setUseRealTimeCandles] = useState(false);

  // Signal files pagination state
  const [selectedFilterModelId, setSelectedFilterModelId] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0); // zero-based

  const [total, setTotal] = useState(0);
  const [signalFiles, setSignalFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadModels();
    fetchSignalFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  async function loadModels() {
    try {
      const modelsList = await ApiClient.getModelsList();
      setModels(modelsList);
      if (modelsList.length > 0 && !selectedModelId) {
        setSelectedModelId(modelsList[0].Id.toString());
      }
    } catch (err) {
      console.error("Failed to load models", err);
      setModels([]);
    }
  }

  async function fetchSignalFiles() {
    setLoading(true);
    const body = {
      ModelId: selectedFilterModelId || undefined,
      Limit: pageSize,
      Offset: page * pageSize,
    };

    try {
      const data = await ApiClient.searchSignalFiles(body);
      setSignalFiles(data.Items || []);
      setTotal(data.TotalCount || 0);
    } catch (err) {
      console.error("Search signal files failed", err);
      setSignalFiles([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function executeModel() {
    if (!selectedModelId) {
      alert("Please select a model to execute");
      return;
    }

    setExecuting(true);
    setExecutionResult(null);
    setExecutionError(null);

    try {
      const result = await ApiClient.executeModel(
        parseInt(selectedModelId),
        useOriginalCtracPackage,
        useRealTimeCandles
      );
      setExecutionResult(result);

      // Refresh signal files list to show the new signal file
      await fetchSignalFiles();

      alert("Model executed successfully!");
    } catch (err) {
      console.error("Model execution failed", err);

      // Set structured error for display
      const errorInfo = {
        message: err.message || "Unknown error occurred",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null
      };
      setExecutionError(errorInfo);
    } finally {
      setExecuting(false);
    }
  }

  function onSearchSignalFiles(e) {
    e.preventDefault();
    setPage(0);
    fetchSignalFiles();
  }

  async function downloadSignalFile(signalFileId) {
    try {
      const { url, filename } = await ApiClient.downloadSignalFile(signalFileId);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download signal file', err);
      alert('Failed to download signal file: ' + err.message);
    }
  }

  function renderSignalFilesTable() {
    if (loading)
      return (
        <p>
          <em>Loading signal files...</em>
        </p>
      );
    if (!signalFiles || signalFiles.length === 0) return <p>No signal files found</p>;

    return (
      <>
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Id</th>
              <th>Model</th>
              <th>Calculation Id</th>
              <th>Tag</th>
              <th>Active</th>
              <th>RT Candles</th>
              <th>Created</th>
              <th>Signals Count</th>
              <th>Action Summary</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {signalFiles.map((sf) => {
              const actionCounts = sf.getSignalActionCounts();
              const isExpanded = expandedSignalFile === sf.Id;
              return (
                <React.Fragment key={sf.Id}>
                  <tr>
                    <td>{sf.Id}</td>
                    <td>{sf.ModelName ? `${sf.ModelName} (ID: ${sf.ModelId})` : `Model #${sf.ModelId}`}</td>
                    <td><small>{sf.CalculationId || '-'}</small></td>
                    <td>{sf.Tag || '-'}</td>
                    <td>{sf.Active ? <span className="badge badge-success" style={{ color: '#000' }}>Yes</span> : <span className="badge badge-secondary" style={{ color: '#000' }}>No</span>}</td>
                    <td>{sf.RealtimeCandlesUsed ? <span className="badge badge-warning" style={{ color: '#000' }}>Yes</span> : <span className="badge badge-secondary" style={{ color: '#000' }}>No</span>}</td>
                    <td>{sf.getFormattedCreateTime()}</td>
                    <td>{sf.getSignalsCount()}</td>
                    <td>
                      <span className="badge badge-success mr-1" style={{ color: '#000' }}>Buy: {actionCounts.Buy}</span>
                      <span className="badge badge-danger mr-1" style={{ color: '#000' }}>Sell: {actionCounts.Sell}</span>
                      <span className="badge badge-secondary mr-1" style={{ color: '#000' }}>Money: {actionCounts.Money}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => setExpandedSignalFile(sf.Id === expandedSignalFile ? null : sf.Id)}
                        >
                          {expandedSignalFile === sf.Id ? "Hide" : "Show"} Signals
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => downloadSignalFile(sf.Id)}
                          title="Download signal file"
                        >
                          Download
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${sf.Id}-expanded`}>
                      <td colSpan="10" style={{ backgroundColor: '#f8f9fa', padding: '15px' }}>
                        {renderExpandedSignalsForRow(sf)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </>
    );
  }

  const [expandedSignalFile, setExpandedSignalFile] = useState(null);

  function renderExpandedSignalsForRow(signalFile) {
    if (!signalFile || !signalFile.Signals || signalFile.Signals.length === 0) {
      return (
        <div className="alert alert-info mb-0">
          No signals found for this signal file.
        </div>
      );
    }

    const formattedSignals = signalFile.getFormattedSignals();

    return (
      <div>
        <h6 className="mb-3">Signals for Signal File #{signalFile.Id}</h6>
        <table className="table table-sm table-bordered mb-0">
          <thead>
            <tr>
              <th>Time</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {formattedSignals.map((signal, index) => (
              <tr key={index}>
                <td>{signal.FormattedDate}</td>
                <td>
                  <span className={`badge ${signal.Action === 'Buy' ? 'badge-success' : signal.Action === 'Sell' ? 'badge-danger' : 'badge-secondary'}`} style={{ color: '#000' }}>
                    {signal.Action}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <h1>Model Execution</h1>

      {/* Model Selection and Execution */}
      <div className="card mb-4">
        <div className="card-header">
          <h5>Execute Model</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <label className="form-label">Select Model</label>
              <select
                className="form-control"
                value={selectedModelId}
                onChange={(e) => setSelectedModelId(e.target.value)}
                disabled={executing}
              >
                <option value="">-- Select a model --</option>
                {models.map((model) => (
                  <option key={model.Id} value={model.Id}>
                    {model.Name || model.FileName || `Model #${model.Id}`} (ID: {model.Id})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <button
                className="btn btn-primary"
                onClick={executeModel}
                disabled={executing || !selectedModelId}
              >
                {executing ? "Executing..." : "Execute Model"}
              </button>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="useOriginalCtracPackage"
                  checked={useOriginalCtracPackage}
                  onChange={(e) => setUseOriginalCtracPackage(e.target.checked)}
                  disabled={executing}
                />
                <label className="form-check-label" htmlFor="useOriginalCtracPackage">
                  Use original CTRAC package
                </label>
              </div>
              <div className="form-check mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="useRealTimeCandles"
                  checked={useRealTimeCandles}
                  onChange={(e) => setUseRealTimeCandles(e.target.checked)}
                  disabled={executing}
                />
                <label className="form-check-label" htmlFor="useRealTimeCandles">
                  Use real-time candles
                </label>
              </div>
            </div>
          </div>

          {/* Execution Result */}
          {executionResult && (
            <div className="alert alert-success mt-3">
              <h6>Execution Result:</h6>
              <p><strong>Execution Duration:</strong> {executionResult.getFormattedExecutionDuration()}</p>
              {executionResult.hasOutput() && (
                <div>
                  <strong>Output:</strong>
                  <pre className="mt-2" style={{ maxHeight: "200px", overflow: "auto", backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "4px" }}>
                    {executionResult.Output}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Execution Error */}
          {executionError && (
            <div className="alert alert-danger mt-3">
              <h6>Execution Error:</h6>
              {executionError.structuredErrors && executionError.structuredErrors.length > 0 ? (
                <div>
                  {executionError.structuredErrors.map((error, index) => (
                    <div key={index} className="mb-2">
                      <strong>Error #{error.Code || 0}:</strong> {error.Message}
                    </div>
                  ))}
                </div>
              ) : (
                <p>{executionError.message}</p>
              )}
              {executionError.status && (
                <small className="text-muted">HTTP Status: {executionError.status}</small>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Signal Files Search and Display */}
      <div className="card">
        <div className="card-header">
          <h5>Signal Files</h5>
        </div>
        <div className="card-body">
          <form onSubmit={onSearchSignalFiles} className="mb-3">
            <div className="row">
              <div className="col-md-4 mb-2">
                <label className="col-form-label">Filter by Model</label>
                <select
                  className="form-control"
                  value={selectedFilterModelId}
                  onChange={(e) => setSelectedFilterModelId(e.target.value)}
                >
                  <option value="">-- All Models --</option>
                  {models.map((model) => (
                    <option key={model.Id} value={model.Id}>
                      {model.Name || model.FileName || `Model #${model.Id}`} (ID: {model.Id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-2 d-flex align-items-end">
                <button className="btn btn-primary" type="submit">
                  Search
                </button>
              </div>
            </div>
          </form>

          <div className="mb-2 d-flex align-items-center">
            <div className="mr-3">Page size:</div>
            <select
              className="form-control mr-3"
              style={{ width: "120px" }}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
            >
              {pageSizeOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <div>
              Page: {page + 1} / {totalPages || 1}
            </div>
          </div>

          <div className="mb-3 d-flex" style={{ gap: "8px", alignItems: "center" }}>
            <button
              className="btn btn-secondary mr-2"
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <button
              className="btn btn-secondary"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Next
            </button>
          </div>

          {renderSignalFilesTable()}

          <div className="mt-2 d-flex" style={{ gap: "8px" }}>
            <button
              className="btn btn-secondary mr-2"
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <button
              className="btn btn-secondary"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelExecution;
